import React, { useState, useEffect } from "react";
import { Button, Spinner, Alert } from "react-bootstrap";
import _ from "lodash";
import HeroSection from "./LandingPageComponents/HeroSection";
import ExplorePunjabCultureSection from "./LandingPageComponents/ExplorePunjabCultureSection";
import BookYourTripSection from "./LandingPageComponents/BookYourTripSection";
import ResortsAndFacilitiesSection from "./LandingPageComponents/ResortsAndFacilitiesSection";
import ExploreSection from "./LandingPageComponents/ExploreSection";
import TravelAcrossPunjabSection from "./LandingPageComponents/TravelAcrossPunjabSection";
import ExploreEventsSection from "./LandingPageComponents/ExploreEventsSection";
import ExplorePunjabMagazineSection from "./LandingPageComponents/ExplorePunjabMagazineSection";
import TourismAttractionsSection from "./LandingPageComponents/TourismAttractionsSection";
import TravellersPhotoGallerySection from "./LandingPageComponents/TravellersPhotoGallerySection";
import VideoSection from "./LandingPageComponents/VideoSection";
import HotelManagementSection from "./LandingPageComponents/HotelManagementSection";
import MetaSection from "./LandingPageComponents/MetaSection";
import { sanitizeData } from ".";

const LandingPageEditor = ({ data, onSave, onCancel, isLoading }) => {
  const defaultStructure = {
    heroSection: {
      isVisible: true,
      slides: [
        {
          title: "",
          subtitle: "",
          image: "",
        },
      ],
    },
    explorePunjabCultureSection: {
      isVisible: true,
      heading: "",
      description: "",
      mainImage: "",
      sideImage: "",
    },
    bookYourTripSection: {
      isVisible: true,
      heading: "",
      description: "",
      buttonText: "",
    },
    resortsAndFacilitiesSection: {
      isVisible: true,
      heading: "",
      description: "",
    },
    exploreSection: {
      isVisible: true,
      heading: "",
      description: "",
      items: [
        {
          name: "",
          image: "",
          buttonText: "",
          link: "",
        },
      ],
    },
    travelAcrossPunjabSection: {
      isVisible: true,
      heading: "",
      description: "",
      image: "",
    },
    exploreEventsSection: {
      isVisible: true,
      heading: "",
      description: "",
      sections: [
        {
          title: "",
          image: "",
          upcomingEventsCount: "",
        },
      ],
    },
    explorePunjabMagazineSection: {
      isVisible: true,
      heading: "",
      description: "",
      backgroundImage: "",
      magazineImage: "",
      downloadLink: "",
    },
    tourismAttractionsSection: {
      isVisible: true,
      heading: "",
      description: "",
      attractions: [
        {
          name: "",
          image: "",
          link: "",
        },
      ],
    },
    travellersPhotoGallerySection: {
      isVisible: true,
      heading: "",
      images: [""],
    },
    videoSection: {
      isVisible: true,
      heading: "",
      description: "",
      videoUrls: [""],
    },
    hotelManagementSection: {
      isVisible: true,
      heading: "",
      description: "",
      image: "",
      externalLink: "",
    },
    meta: {
      title: "",
      description: "",
    },
  };

  const [formData, setFormData] = useState(defaultStructure);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "info" });

  useEffect(() => {
    if (data?.content) {
      setFormData({ ...defaultStructure, ...data.content });
    }
  }, [data]);

  const handleSectionUpdate = (sectionName, sectionData) => {
    setFormData(prev => ({
      ...prev,
      [sectionName]: sectionData
    }));
  };

  const handleSave = () => {
    const sanitizedData = sanitizeData(formData, defaultStructure);
    onSave({ pageName: data.pageName, content: sanitizedData });
  };

  return (
    <div className="col-12">
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() => setAlert({ show: false, message: "", variant: "info" })}
          className="mb-3"
        >
          {alert.message}
        </Alert>
      )}

      <div className="card p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <Button variant="link" className="p-0 me-3" onClick={onCancel}>
              ← Back
            </Button>
            <h4 className="m-0">Edit Landing Page</h4>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <HeroSection
              data={formData.heroSection}
              onUpdate={(data) => handleSectionUpdate("heroSection", data)}
            />

            <ExplorePunjabCultureSection
              data={formData.explorePunjabCultureSection}
              onUpdate={(data) => handleSectionUpdate("explorePunjabCultureSection", data)}
            />

            <BookYourTripSection
              data={formData.bookYourTripSection}
              onUpdate={(data) => handleSectionUpdate("bookYourTripSection", data)}
            />

            <ResortsAndFacilitiesSection
              data={formData.resortsAndFacilitiesSection}
              onUpdate={(data) => handleSectionUpdate("resortsAndFacilitiesSection", data)}
            />

            <ExploreSection
              data={formData.exploreSection}
              onUpdate={(data) => handleSectionUpdate("exploreSection", data)}
            />

            <TravelAcrossPunjabSection
              data={formData.travelAcrossPunjabSection}
              onUpdate={(data) => handleSectionUpdate("travelAcrossPunjabSection", data)}
            />

            <ExploreEventsSection
              data={formData.exploreEventsSection}
              onUpdate={(data) => handleSectionUpdate("exploreEventsSection", data)}
            />

            <ExplorePunjabMagazineSection
              data={formData.explorePunjabMagazineSection}
              onUpdate={(data) => handleSectionUpdate("explorePunjabMagazineSection", data)}
            />

            <TourismAttractionsSection
              data={formData.tourismAttractionsSection}
              onUpdate={(data) => handleSectionUpdate("tourismAttractionsSection", data)}
            />

            <TravellersPhotoGallerySection
              data={formData.travellersPhotoGallerySection}
              onUpdate={(data) => handleSectionUpdate("travellersPhotoGallerySection", data)}
            />

            <VideoSection
              data={formData.videoSection}
              onUpdate={(data) => handleSectionUpdate("videoSection", data)}
            />

            <HotelManagementSection
              data={formData.hotelManagementSection}
              onUpdate={(data) => handleSectionUpdate("hotelManagementSection", data)}
            />

            <MetaSection
              data={formData.meta}
              onUpdate={(data) => handleSectionUpdate("meta", data)}
            />

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageEditor;
